import type { IconDefinition } from '@fortawesome/pro-light-svg-icons';
import {
  faCheck,
  faDownload,
  faExclamationTriangle,
  faFileAlt,
  faSpinnerThird,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { TImporterState } from '@feathr/blackbox';
import type { Importer } from '@feathr/blackbox';
import { Avatar, Button, Card, Pill, Spinner, Time } from '@feathr/components';
import { useFlags, useLocalUrl, useStore } from '@feathr/extender/state';
import { getImportDisplayName } from '@feathr/extender/utils/importDisplayName';
import { isImportsRevampEnabled } from '@feathr/extender/utils/importsRevampFlags';
import { TimeFormat } from '@feathr/hooks';

import EventImportCardActions from './EventImportCardActions';
import EventImportRunDetails from './EventImportRunDetails';

import * as styles from './EventImportCard.css';

import noImg from 'images/no-img.png';

interface IImportCardProps {
  importer: Importer;
}

const stateIcons: Record<TImporterState, IconDefinition> = {
  draft: faFileAlt,
  pending: faSpinnerThird,
  // Nothing writes `processing` any more, but older imports can still carry it.
  processing: faSpinnerThird,
  complete: faCheck,
  failed: faExclamationTriangle,
};

const DownloadButton = ({ importer }: IImportCardProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Button
      className={styles.button}
      href={`${BLACKBOX_URL}events/${importer.get('event')}/partners/imports/${importer.id}`}
      prefix={<FontAwesomeIcon icon={faDownload} size={'2x'} />}
      target={'_blank'}
    >
      {t('Download import file')}
    </Button>
  );
};

const FinishButton = ({ importer }: IImportCardProps): JSX.Element => {
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { t } = useTranslation();

  return (
    <Button
      className={styles.button}
      onClick={() => history.push(localUrl(importer.getItemUrl()))}
      prefix={<FontAwesomeIcon icon={faCheck} size={'2x'} />}
    >
      {t('Finish')}
    </Button>
  );
};

function EventImportCard({ importer }: IImportCardProps): JSX.Element {
  const { Tags, Users } = useStore();
  const { t } = useTranslation();
  const isRevampEnabled = isImportsRevampEnabled(useFlags());
  const tags = Tags.list({
    filters: { is_archived__ne: true, collection: 'Partner', id__in: importer.get('tag_ids') },
    ordering: ['-date_created'],
  });
  if (tags.isPending) {
    return <Spinner />;
  }
  const created_by = Users.get(importer.get('created_by'));
  const userName = created_by.name || t('An unknown user');
  const importName = getImportDisplayName(importer, t);
  const state: TImporterState = importer.get('state');
  const stateLabels: Record<TImporterState, string> = {
    draft: t('Draft'),
    pending: t('Processing'),
    processing: t('Processing'),
    complete: t('Complete'),
    failed: t('Failed'),
  };
  const legacyAction =
    state === 'draft' ? (
      <FinishButton importer={importer} />
    ) : (
      <DownloadButton importer={importer} />
    );
  return (
    <Card className={styles.card} name={'event-import-card'}>
      <div className={styles.meta}>
        <Avatar image={created_by.get('picture') || noImg} size={'large'} />
        <div className={styles.subdued}>
          <Time format={TimeFormat.shortDate} timestamp={importer.get('date_created')} />
        </div>
      </div>
      <p>
        {state === 'draft'
          ? t('{{- userName}} started "{{- importName}}"', { userName, importName })
          : t('{{- userName}} uploaded "{{- importName}}"', { userName, importName })}
      </p>
      <div className={styles.tags}>
        <b>{t('Tags:')}</b>
        {tags.models.length > 0 ? (
          tags.models.map((tag) => (
            <Pill key={tag.id} theme={'neutral'}>
              {tag.name}
            </Pill>
          ))
        ) : (
          <span className={styles.subdued}>{t('None')}</span>
        )}
      </div>
      <FontAwesomeIcon
        className={classNames(
          styles.icon,
          { [styles.draft]: state === 'draft' },
          { [styles.complete]: state === 'complete' },
          { [styles.failed]: state === 'failed' },
        )}
        icon={stateIcons[state]}
        spin={state === 'pending' || state === 'processing'}
      />
      <span>{stateLabels[state]}</span>
      {isRevampEnabled ? (
        <>
          <EventImportRunDetails importer={importer} />
          <EventImportCardActions importer={importer} />
        </>
      ) : (
        legacyAction
      )}
    </Card>
  );
}

export default observer(EventImportCard);
